<template>
	<v-card>
		<v-toolbar flat color="dark" dark>
			<v-toolbar-title>
				<span class="pl-2" v-if="numericId > 0">Portal gebruiker bewerken</span>
				<span class="pl-2" v-else>Nieuwe portal gebruiker</span>
			</v-toolbar-title>
		</v-toolbar>
		<v-card-text>
			<v-alert v-if="formError" type="error" dense outlined class="mb-4">{{ formError }}</v-alert>
			<v-alert v-if="numericId === 0" type="info" dense outlined class="mb-4">
				Het startwachtwoord wordt automatisch gegenereerd en per e-mail naar het gebruikersnaam-adres (school e-mail) verzonden. Bij eerste login moet de gebruiker het wachtwoord wijzigen.
			</v-alert>
			<v-alert v-if="numericId > 0 && userIsDeleted" type="warning" dense outlined class="mb-4">
				Deze gebruiker is gedeactiveerd en kan niet inloggen. Klik op <strong>Activeren</strong> om het account weer toe te staan.
			</v-alert>
			<v-form ref="form" v-model="formValid">
				<v-row>
					<v-col cols="12" sm="6">
						<v-select
							dense
							filled
							:items="roles"
							item-text="name"
							item-value="id"
							v-model="form.role_id"
							label="Rol *"
							:rules="[rules.requiredRole]"
						></v-select>
						<v-text-field dense filled v-model="form.firstname" label="Voornaam"></v-text-field>
						<v-text-field dense filled v-model="form.surname" label="Achternaam"></v-text-field>
						<v-text-field dense filled v-model="form.display_name" label="Weergavenaam"></v-text-field>
					</v-col>
					<v-col cols="12" sm="6">
						<v-text-field
							dense
							filled
							v-model="form.username"
							label="Gebruikersnaam (inlog) *"
							:rules="[rules.required]"
						></v-text-field>
						<v-text-field
							v-if="numericId > 0"
							dense
							filled
							v-model="form.password"
							label="Nieuw wachtwoord"
							type="password"
							:rules="optionalPasswordRules"
							hint="Laat leeg om het huidige wachtwoord te behouden."
							persistent-hint
						></v-text-field>
					</v-col>
				</v-row>
			</v-form>
		</v-card-text>
		<v-card-actions>
			<v-btn color="primary" depressed :loading="saving" @click="submit">Opslaan</v-btn>
			<v-btn color="dark" depressed @click="$router.go(-1)">Annuleren</v-btn>
		</v-card-actions>
	</v-card>
</template>

<script>
import Axios from "axios";
import { API_ROOT } from "../../config";

const ax = Axios.create({
	baseURL: API_ROOT["osbod_salary"],
	timeout: 60000
});

export default {
	name: "PortalUsersAddEdit",
	props: ["id"],
	data() {
		return {
			formValid: false,
			saving: false,
			toggleSaving: false,
			formError: "",
			roles: [],
			userDeletedFlag: false,
			form: {
				role_id: null,
				firstname: "",
				surname: "",
				display_name: "",
				username: "",
				password: ""
			},
			rules: {
				required: function(v) {
					return (v != null && String(v).trim() !== "") || "Verplicht veld";
				},
				requiredRole: function(v) {
					return (v != null && v !== "") || "Selecteer een rol";
				}
			}
		};
	},
	computed: {
		numericId() {
			var n = parseInt(this.id, 10);
			return isNaN(n) ? 0 : n;
		},
		userIsDeleted() {
			return this.userDeletedFlag;
		},
		optionalPasswordRules() {
			return [
				function(v) {
					return !v || String(v).length >= 6 || "Minimaal 6 tekens indien ingevuld";
				}
			];
		}
	},
	created() {
		this.fetchRoles();
		if (this.numericId > 0) {
			this.fetchUser();
		}
	},
	methods: {
		fetchRoles() {
			var self = this;
			ax.get("/role/all")
				.then(function(res) {
					self.roles = Array.isArray(res.data) ? res.data : [];
				})
				.catch(function() {
					self.formError = "Rollen konden niet worden geladen.";
				});
		},
		fetchUser() {
			var self = this;
			ax.get("/users/single/" + this.numericId)
				.then(function(res) {
					var u = res.data;
					if (!u || !u.id) {
						self.formError = "Gebruiker niet gevonden.";
						return;
					}
					self.form.role_id = u.role_id;
					self.form.firstname = u.firstname || "";
					self.form.surname = u.surname || "";
					self.form.display_name = u.display_name || "";
					self.form.username = u.username || "";
					self.form.password = "";
				})
				.catch(function() {
					self.formError = "Gebruiker kon niet worden geladen.";
				});
		},
		deactivateUser() {
			var self = this;
			if (!this.numericId) return;
			if (!window.confirm("Gebruiker deactiveren? Deze kan daarna niet meer inloggen.")) {
				return;
			}
			this.toggleSaving = true;
			this.formError = "";
			ax.post("/users/update/" + this.numericId, { deleted: 1 })
				.then(function() {
					self.userDeletedFlag = true;
				})
				.catch(function(err) {
					var msg =
						(err.response && err.response.data && err.response.data.msg) ||
						"Deactiveren is mislukt.";
					self.formError = msg;
				})
				.finally(function() {
					self.toggleSaving = false;
				});
		},
		activateUser() {
			var self = this;
			if (!this.numericId) return;
			this.toggleSaving = true;
			this.formError = "";
			ax.post("/users/update/" + this.numericId, { deleted: null })
				.then(function() {
					self.userDeletedFlag = false;
				})
				.catch(function(err) {
					var msg =
						(err.response && err.response.data && err.response.data.msg) ||
						"Activeren is mislukt.";
					self.formError = msg;
				})
				.finally(function() {
					self.toggleSaving = false;
				});
		},
		submit() {
			var self = this;
			this.formError = "";
			if (this.$refs.form && !this.$refs.form.validate()) {
				return;
			}
			this.saving = true;
			var payload = {
				role_id: this.form.role_id,
				firstname: this.form.firstname,
				surname: this.form.surname,
				display_name: this.form.display_name,
				username: this.form.username
			};
			if (this.numericId > 0 && this.form.password) {
				payload.password = this.form.password;
			}
			if (this.numericId > 0) {
				ax.post("/users/update/" + this.numericId, payload)
					.then(function() {
						self.$router.push({ path: "/schooladmin/portal_users" });
					})
					.catch(function(err) {
						var msg =
							(err.response && err.response.data && err.response.data.msg) ||
							"Opslaan is mislukt.";
						self.formError = msg;
					})
					.finally(function() {
						self.saving = false;
					});
			} else {
				ax.post("/users/create", payload)
					.then(function() {
						self.$router.push({ path: "/schooladmin/portal_users" });
					})
					.catch(function(err) {
						var msg =
							(err.response && err.response.data && err.response.data.msg) ||
							"Aanmaken is mislukt (bestaat de gebruikersnaam al?).";
						self.formError = msg;
					})
					.finally(function() {
						self.saving = false;
					});
			}
		}
	}
};
</script>
